import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, KeyRound, LogOut, Power, Unlock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { EmptyState, Field, PageHeader, StatusBadge } from "@/components/shared/Common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { userStatus } from "./Users";
import { UserRolesTab, UserScopesTab } from "./UserAccessTabs";
import { AuditTable } from "./Audit";

const ProfileTab = ({ user, canEdit, onSaved }) => {
  const [f, setF] = useState({ name: user.name, email: user.email, username: user.username || "" });
  const save = async (e) => {
    e.preventDefault();
    try { await api.patch(`/admin/users/${user.id}`, { ...f, username: f.username || null }); toast.success("Datos actualizados"); onSaved(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={save} className="syc-card p-6 space-y-4 max-w-2xl" data-testid="user-profile-tab">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Nombre"><Input data-testid="edit-user-name-input" disabled={!canEdit} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        <Field label="Usuario"><Input data-testid="edit-user-username-input" disabled={!canEdit} value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} /></Field>
      </div>
      <Field label="Email"><Input data-testid="edit-user-email-input" disabled={!canEdit} type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
      <Field label="Trabajador asociado" hint="La ficha del trabajador se habilita con RR.HH. (Fase 3).">
        <Input disabled value={user.employee_id || "Sin trabajador asociado"} data-testid="edit-user-employee-input" />
      </Field>
      <div className="text-xs text-[#5b6656]">Creado: {fmtDateTime(user.created_at)} · Último ingreso: {fmtDateTime(user.last_login_at)}</div>
      {canEdit && <Button data-testid="save-user-profile-btn" className="rounded-full">Guardar cambios</Button>}
    </form>
  );
};

const SessionsTab = ({ sessions }) => (
  <div className="syc-card overflow-hidden" data-testid="user-sessions-tab">
    {sessions.length === 0 ? <EmptyState title="Sin sesiones activas" /> : sessions.map((s) => (
      <div key={s.id} className="px-5 py-3 border-b border-[#f0f2ee] last:border-0 text-sm">
        <div className="font-mono text-xs">{s.ip}</div>
        <div className="text-xs text-[#5b6656] truncate">{s.user_agent}</div>
        <div className="text-xs text-[#8a9584]">Inicio {fmtDateTime(s.created_at)} · Última actividad {fmtDateTime(s.last_seen)}</div>
      </div>
    ))}
  </div>
);

const ResetDialog = ({ open, onOpenChange, userId }) => {
  const [temp, setTemp] = useState("");
  const run = async (mode) => {
    try {
      const { data } = await api.post(`/admin/users/${userId}/reset-password`, { mode, temp_password: mode === "temporal" ? temp : null });
      toast.success(data.message); onOpenChange(false); setTemp("");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="reset-password-dialog">
        <DialogHeader><DialogTitle>Resetear contraseña</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Button data-testid="reset-by-email-btn" variant="outline" className="w-full" onClick={() => run("email")}>Enviar enlace de recuperación por email</Button>
          <div className="text-center text-xs text-[#8a9584]">o asignar contraseña temporal</div>
          <Field label="Contraseña temporal" hint="El usuario deberá cambiarla. Sus sesiones se cerrarán.">
            <Input data-testid="reset-temp-password-input" value={temp} onChange={(e) => setTemp(e.target.value)} />
          </Field>
        </div>
        <DialogFooter><Button data-testid="reset-temp-submit-btn" disabled={!temp} onClick={() => run("temporal")}>Asignar temporal</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function UserDetail() {
  const { id } = useParams();
  const { can, user: me } = useAuth();
  const [u, setU] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const load = useCallback(() => api.get(`/admin/users/${id}`).then((r) => setU(r.data)).catch((e) => toast.error(errMsg(e))), [id]);
  useEffect(() => { load(); }, [load]);
  if (!u) return <div className="text-[#5b6656]">Cargando…</div>;

  const st = userStatus(u);
  const act = async (path, msg) => {
    try { await api.post(`/admin/users/${id}/${path}`); toast.success(msg); load(); }
    catch (e) { toast.error(errMsg(e)); }
    setConfirm(null);
  };
  const actions = (
    <>
      {u.locked && can("admin.usuarios.activar") && <Button data-testid="unlock-user-btn" variant="outline" className="rounded-full" onClick={() => act("unlock", "Usuario desbloqueado")}><Unlock size={15} className="mr-1" />Desbloquear</Button>}
      {can("admin.usuarios.reset_password") && <Button data-testid="reset-password-btn" variant="outline" className="rounded-full" onClick={() => setResetOpen(true)}><KeyRound size={15} className="mr-1" />Resetear contraseña</Button>}
      {can("admin.usuarios.cerrar_sesiones") && <Button data-testid="revoke-sessions-btn" variant="outline" className="rounded-full" onClick={() => setConfirm({ path: "revoke-sessions", title: "Cerrar todas las sesiones", msg: "Sesiones cerradas" })}><LogOut size={15} className="mr-1" />Cerrar sesiones</Button>}
      {can("admin.usuarios.activar") && u.id !== me.id && (u.status === "activo"
        ? <Button data-testid="deactivate-user-btn" variant="outline" className="rounded-full text-[#b42318] border-[#f5c2c0]" onClick={() => setConfirm({ path: "deactivate", title: "Desactivar usuario", msg: "Usuario desactivado" })}><Power size={15} className="mr-1" />Desactivar</Button>
        : <Button data-testid="activate-user-btn" className="rounded-full" onClick={() => act("activate", "Usuario activado")}><Power size={15} className="mr-1" />Activar</Button>)}
    </>
  );

  return (
    <div data-testid="user-detail-page">
      <Link to="/admin/usuarios" data-testid="back-to-users-link" className="inline-flex items-center gap-1 text-sm font-semibold text-[#258F00] mb-4"><ArrowLeft size={15} />Usuarios</Link>
      <PageHeader eyebrow={<StatusBadge testid="user-status-badge" status={st.s} label={st.l} />} title={u.name} subtitle={`${u.email}${u.username ? " · " + u.username : ""}`} actions={actions} />
      <Tabs defaultValue="datos">
        <TabsList className="mb-5 flex-wrap h-auto">
          <TabsTrigger data-testid="tab-datos" value="datos">Datos</TabsTrigger>
          <TabsTrigger data-testid="tab-roles" value="roles">Roles</TabsTrigger>
          <TabsTrigger data-testid="tab-alcances" value="alcances">Alcances</TabsTrigger>
          <TabsTrigger data-testid="tab-sesiones" value="sesiones">Sesiones ({u.sessions.length})</TabsTrigger>
          {can("admin.auditoria.ver") && <TabsTrigger data-testid="tab-historial" value="historial">Historial</TabsTrigger>}
        </TabsList>
        <TabsContent value="datos"><ProfileTab user={u} canEdit={can("admin.usuarios.editar")} onSaved={load} /></TabsContent>
        <TabsContent value="roles"><UserRolesTab user={u} canEdit={can("admin.usuarios.editar") && can("admin.roles.gestionar")} onSaved={load} /></TabsContent>
        <TabsContent value="alcances"><UserScopesTab user={u} canEdit={can("admin.alcance.asignar")} onSaved={load} /></TabsContent>
        <TabsContent value="sesiones"><SessionsTab sessions={u.sessions} /></TabsContent>
        {can("admin.auditoria.ver") && <TabsContent value="historial"><AuditTable fixed={{ resource_type: "user", resource_id: u.id }} /></TabsContent>}
      </Tabs>
      <ResetDialog open={resetOpen} onOpenChange={setResetOpen} userId={id} />
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader><AlertDialogTitle>{confirm?.title}</AlertDialogTitle>
            <AlertDialogDescription>Esta acción quedará registrada en auditoría. Las sesiones activas del usuario se cerrarán.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-accept-btn" onClick={() => act(confirm.path, confirm.msg)}>Confirmar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
